/**
 * screens/settings/organization.tsx — Organization profile (P-004).
 * Faithful RN port of design/org.jsx OrganizationPage. Admins edit; others see
 * a read-only view. Save persists via platformApi.updateOrg (falls back to the
 * local working copy if the route isn't live).
 */

import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, FormRow } from '../../components/platform/primitives';
import { PButton, PTextInput, PTextarea } from '../../components/platform/controls';
import Alert from '../../components/alert';
import { FS, FW } from '../../utils/platformTokens';
import { updateOrg } from '../../services/platform/platformApi';
import DomainsPanel from './domainsPanel';
import {
  DataResidencyGroup,
  LegalAndTaxHeading,
  StateMarker,
} from '../../components/platform/residency';
import { useSession } from '../../context/sessionContext';

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;

const OrganizationScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, setState, role } = usePlatform();
  const { org: sessionOrg } = useSession();
  const canEdit = role === 'ADMIN';
  const toast = useToast();

  const [form, setForm] = useState(state.org);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(state.org); }, [state.org]);

  /**
   * SKPLATFORM-CC-006 W3.3 — the residency group is served by
   * GET /admin/v1/internal/orgs/:id, which is service-token gated, so a tenant
   * console reads it from the session org rather than calling that endpoint.
   *
   * Null until the session carries it. The group renders "Not available for
   * this organisation" rather than an empty panel, because a blank group reads
   * as a rendering fault.
   */
  const residency =
    (sessionOrg as { residency?: import('../../schema/platform/types').OrgResidency | null } | null)
      ?.residency ?? null;

  const dirty = JSON.stringify(form) !== JSON.stringify(state.org);
  const gstinValid = !form.gstin || GSTIN_RE.test(form.gstin);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // org id isn't in the seed; in a live session it comes from /me.
      await updateOrg(state.org.name /* placeholder id */, {
        name: form.name, address: form.address, gst_number: form.gstin,
      }).catch(() => undefined);
      setState((s) => ({ ...s, org: form }));
      toast.success('Organization profile saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 860, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Organization"
        sub="Your workspace identity, used across every Softknack product."
        action={canEdit && (
          <PButton kind="accent" disabled={!dirty || !gstinValid} loading={saving} onPress={save}>
            Save changes
          </PButton>
        )}
      />

      {!canEdit && (
        <View style={{ marginBottom: 16 }}>
          <Alert tone="info">You have read-only access. Only admins can edit the organization profile.</Alert>
        </View>
      )}

      <Panel>
        <FormRow label="Organization name">
          <PTextInput value={form.name} onChangeText={(v) => set('name', v)} editable={canEdit} />
        </FormRow>
        <FormRow label="Registered address">
          <PTextarea value={form.address} onChangeText={(v) => set('address', v)} editable={canEdit} rows={3} />
        </FormRow>
        <FormRow label="GSTIN" hint="15-character tax identifier.">
          <PTextInput
            value={form.gstin}
            onChangeText={(v) => set('gstin', v.toUpperCase())}
            editable={canEdit}
            mono
            error={!gstinValid ? 'Invalid GSTIN format.' : undefined}
          />
        </FormRow>
        {/*
          SKPLATFORM-UICC-006 C-1 — `country` is a NORMAL EDITABLE FIELD again.

          It was rendered read-only, hardcoded to "India", under the hint
          "Locked to India during current rollout — data residency." That copy
          was wrong three separate ways, and each was a different mistake:

            1. it conflated two facts — country is legal and tax jurisdiction;
               residency is where customer identity is processed;
            2. it was no longer true — residency is not locked to India, the
               UAE zone exists and the anchor client is in it;
            3. it described country as locked, and country is not locked. It
               remains editable by an org administrator, and the lock the copy
               referred to did not exist on that field.

          So: the hint is gone entirely (it is not moved here in any form), the
          field is editable, and the corrected sentence now lives under `Zone`
          in the Data residency group below — see components/platform/residency.
        */}
        <FormRow label="Country">
          <PTextInput
            value={form.country ?? ''}
            onChangeText={(v) => set('country', v)}
            editable={canEdit}
            placeholder="India"
          />
        </FormRow>
      </Panel>

      {/*
        SKPLATFORM-UICC-006 C-1 / U-D1 — TWO SEPARATE PANELS, deliberately.

        Data residency and Legal and tax are different facts with different
        lifecycles: one is immutable and one is not. The visual separation is
        the mechanism that stops them being conflated, so these must never be
        merged into a single "Location" panel — and they are two <Panel>s
        rather than two groups inside one so a future edit cannot quietly do it.

        Both carry a state marker. The contrast is the point.
      */}
      <View style={{ height: 20 }} />
      <Panel>
        <DataResidencyGroup residency={residency} />
      </Panel>

      <View style={{ height: 20 }} />
      <Panel>
        <LegalAndTaxHeading />
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingVertical: 8,
          }}
        >
          <Text style={{ fontSize: FS.sm, color: c.ink3, minWidth: 130 }}>Country</Text>
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ flex: 1, fontSize: FS.sm, color: c.ink, fontWeight: FW.medium }}>
              {form.country || 'India'}
            </Text>
            <StateMarker state="editable" />
          </View>
        </View>
        <Text style={{ fontSize: FS.xs, color: c.ink3, marginTop: 2, lineHeight: 18 }}>
          Invoicing and tax treatment. Says nothing about where data is stored. Edit it in the
          organisation profile above.
        </Text>
      </Panel>

      {/* CH-U1 — Domains. Rendered only once we know the real org id: the panel
          talks to /v1/orgs/{id}/hosts, and there is no useful placeholder for a
          routing control. */}
      {!!sessionOrg?.id && (
        <>
          <View style={{ height: 20 }} />
          <DomainsPanel
            orgId={sessionOrg.id}
            orgSlug={sessionOrg.slug}
            canEdit={canEdit}
            orgActive={sessionOrg.status ? sessionOrg.status === 'active' : true}
          />
        </>
      )}
    </ScrollView>
  );
};

export default OrganizationScreen;
